import { useId } from 'react';
import { EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import { RadioCardGroup } from '@/shared/ui/RadioCardGroup';
import { WorkspacesField } from '@/shared/ui/WorkspacesField';
import { KnowledgeBaseFormFieldV2 } from './KnowledgeBaseFormFieldV2';
import { useKBVisibilityOptions } from '../KnowledgeBaseFormSpaceVisibility/useKBVisibilityOptions';
import type { FormSpaceVisibilityV2Props } from '../KnowledgeBaseFormSpaceVisibility/KnowledgeBaseFormSpaceVisibility.types';

/**
 * Tessra twin of `KnowledgeBaseFormSpaceVisibility`. The props are a superset, so the two are
 * interchangeable at the call site.
 */
export const KnowledgeBaseFormSpaceVisibilityV2 = ({
  spaceVisibility,
  setSpaceVisibility,
  workspaces,
}: FormSpaceVisibilityV2Props) => {
  const { t } = useTranslation('knowledge');

  const fieldId = useId();
  const helpId = useId();
  const visibilityOptions = useKBVisibilityOptions({
    canSelectWorkspaces: !!workspaces,
    includeCurrentWorkspace: workspaces?.includeCurrentWorkspace,
  });

  // Nothing is selected on a fresh create, so the most privileged option the
  // user may pick is chosen for them.
  useMountEffect(() => {
    const defaultOption = visibilityOptions.find((option) => option.checkedByDefault);
    if (defaultOption && spaceVisibility == null) {
      setSpaceVisibility(defaultOption.value);
    }
    return undefined;
  });

  return (
    <KnowledgeBaseFormFieldV2
      icon={EyeIcon}
      title={t('knowledgeBases.knowledgeBaseFormSpaceVisibility.spaceVisibility')}
      description={t('knowledgeBases.knowledgeBaseFormSpaceVisibility.setTheSpaceVisibilityTo')}
      required
      fieldId={fieldId}
      descriptionId={helpId}
    >
      <RadioCardGroup
        aria-labelledby={`${fieldId}-label`}
        aria-describedby={helpId}
        options={visibilityOptions}
        value={spaceVisibility}
        onChange={setSpaceVisibility}
      />
      {/* Outside the radio group, which holds only radios, so a screen reader does not announce
          the picker as one of the choices. */}
      {workspaces && spaceVisibility === 'workspaces' && (
        <WorkspacesField
          options={workspaces.options}
          value={workspaces.value}
          onChange={workspaces.onChange}
          lockedWorkspaceId={workspaces.lockedWorkspaceId}
          helperText={t(
            'knowledgeBases.knowledgeBaseFormSpaceVisibility.onlyAgentsInSelectedWorkspaces',
          )}
        />
      )}
    </KnowledgeBaseFormFieldV2>
  );
};

KnowledgeBaseFormSpaceVisibilityV2.displayName = 'KnowledgeBaseFormSpaceVisibilityV2';
